import { calculateBudgetFromImpressions, calculateImpressionsFromBudget } from '@mntn/cpm'
import { renderImpressions } from '@mntn/formatters'
import { Image } from '@mntn/select-components'
import type { ButtonSize } from '@mntn/ui-components'
import { Icon, Input, Popover, PopoverContent, PopoverTrigger, Text, useToast } from '@mntn/ui-components'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import { DATE_FORMATS } from '@/components/shared/consts'
import type { AddBudgetResponse, OrderItem } from '@/flight-deck-api/services/order-items.service'
import { useGetOffering } from '@/hooks/api/use-get-offering'
import { AddBudgetButton } from './add-budget-button'
import { Button } from './button'
import { SLATE_THEME_CLASSNAME } from './mfe-theme-wrapper'
import { formatMoney, getOfferingText } from './utils'

export interface AddBudgetSubmitData {
  offeringVersionId: number
  budget: number
}

interface AddBudgetPopoverProps {
  orderItem: OrderItem
  onSubmit: (data: AddBudgetSubmitData) => Promise<AddBudgetResponse>
  isSubmitting?: boolean
  size?: ButtonSize
  isDisabled?: boolean
  className?: string
  side?: 'top' | 'right' | 'bottom' | 'left'
  buttonLabel?: string
}

export function AddBudgetPopover(props: AddBudgetPopoverProps) {
  const { data: latestOffering, isLoading: isLatestOfferingLoading } = useGetOffering({
    offeringId: props.orderItem.offeringId,
    includePurchasedManagedSales: true,
  })

  if (isLatestOfferingLoading) return null

  return (
    <AddBudgetPopoverView
      {...props}
      cpm={latestOffering.storefrontCpm}
      offeringVersionId={latestOffering.offeringVersionId}
    />
  )
}

interface AddBudgetPopoverViewProps extends AddBudgetPopoverProps {
  offeringVersionId: number
  cpm: number
}

function AddBudgetPopoverView({
  offeringVersionId,
  orderItem,
  cpm,
  onSubmit,
  isSubmitting = false,
  size = 'tiny',
  isDisabled = false,
  className,
  side = 'bottom',
  buttonLabel,
}: AddBudgetPopoverViewProps) {
  const [open, setOpen] = useState(false)
  const [budgetInput, setBudgetInput] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const { toast } = useToast()

  const budget = useMemo(() => Number.parseFloat(budgetInput) || 0, [budgetInput])
  const impressions = calculateImpressionsFromBudget({ budget, cpm })
  const impressionsInput = impressions > 0 ? renderImpressions(impressions) : ''

  const handleBudgetChange = (value: string) => {
    setBudgetInput(value)
  }

  const handleImpressionsChange = (value: string) => {
    const imps = Number(value.replace(/[^\d.]/g, '')) || 0
    const computed = calculateBudgetFromImpressions(imps, cpm)
    setBudgetInput(computed > 0 ? computed.toFixed(2) : '')
  }

  const canSubmit = budget > 0 && impressions > 0 && !isSubmitting

  const handleClose = () => {
    setBudgetInput('')
    setIsSuccess(false)
    setOpen(false)
  }

  const showPurchaseError = () => {
    toast({
      title: 'Something went wrong',
      icon: 'destructive',
    })
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    try {
      const response = await onSubmit({ offeringVersionId, budget })
      if (response.status !== 200) {
        showPurchaseError()
        return
      }
      setIsSuccess(true)
    } catch {
      showPurchaseError()
    }
  }

  return (
    <Popover open={open} onOpenChange={next => (next ? setOpen(true) : handleClose())}>
      <PopoverTrigger asChild>
        <AddBudgetButton
          size={size}
          isDisabled={isDisabled}
          isLoading={isSubmitting}
          className={className}
          label={buttonLabel}
        />
      </PopoverTrigger>
      <PopoverContent
        className="bg-background-inverse border border-border-inverse p-0 text-header-inverse w-[calc(100vw-32px)] max-w-[660px] rounded-md shadow-lg z-[1050]"
        wrapperClassName={SLATE_THEME_CLASSNAME}
        side={side}
        align="center"
        sideOffset={8}
        collisionPadding={16}
      >
        <AddBudgetPopoverContent
          orderItem={orderItem}
          cpm={cpm}
          budget={budget}
          budgetInput={budgetInput}
          impressionsInput={impressionsInput}
          onBudgetChange={handleBudgetChange}
          onImpressionsChange={handleImpressionsChange}
          onSubmit={handleSubmit}
          onClose={handleClose}
          canSubmit={canSubmit}
          isSuccess={isSuccess}
          isSubmitting={isSubmitting}
        />
      </PopoverContent>
    </Popover>
  )
}

interface AddBudgetPopoverContentProps {
  orderItem: OrderItem
  cpm: number
  budget: number
  budgetInput: string
  impressionsInput: string
  onBudgetChange: (value: string) => void
  onImpressionsChange: (value: string) => void
  onSubmit: () => void
  onClose: () => void
  canSubmit: boolean
  isSuccess: boolean
  isSubmitting: boolean
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
const AddBudgetPopoverContent: React.FC<AddBudgetPopoverContentProps> = ({
  orderItem,
  cpm,
  budget,
  budgetInput,
  impressionsInput,
  onBudgetChange,
  onImpressionsChange,
  onSubmit,
  onClose,
  canSubmit,
  isSuccess,
  isSubmitting,
}) => {
  if (isSuccess) {
    return <AddBudgetSuccess budget={budget} onClose={onClose} />
  }

  return (
    <>
      {/* Header row: title/subtitle + close button */}
      <div className="flex gap-[10px] items-start w-full">
        <div className="flex flex-1 flex-col p-4">
          <Text variant="h5" className="text-header-inverse">
            Add Budget to Deal
          </Text>
          <Text variant="base-sm" className="text-primary-inverse">
            Purchase more of this deal by adding cost or impressions.
          </Text>
        </div>
        <Button
          variant="ghost"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 size-14 flex items-center justify-center text-primary-inverse hover:text-header-inverse hover:bg-transparent p-0"
        >
          <Icon name="X" className="size-[18px] text-primary-inverse" />
        </Button>
      </div>

      {/* Deal info + form */}
      <div className="flex flex-col items-start">
        <DealInfo orderItem={orderItem} cpm={cpm} />
        <BudgetForm
          budgetInput={budgetInput}
          impressionsInput={impressionsInput}
          onBudgetChange={onBudgetChange}
          onImpressionsChange={onImpressionsChange}
          onSubmit={onSubmit}
          canSubmit={canSubmit}
          isSubmitting={isSubmitting}
        />
      </div>
    </>
  )
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
const AddBudgetSuccess: React.FC<{
  budget: number
  onClose: () => void
}> = ({ budget, onClose }) => (
  <div className="relative flex min-h-[322px] w-full flex-col items-center justify-center px-6 py-8 text-center">
    <Button
      variant="ghost"
      onClick={onClose}
      aria-label="Close"
      className="absolute right-0 top-0 size-14 flex items-center justify-center text-primary-inverse hover:text-header-inverse hover:bg-transparent p-0"
    >
      <Icon name="X" className="size-[18px]" />
    </Button>
    <div className="flex size-12 items-center justify-center rounded-full bg-secondary-accent-inverse">
      <Icon name="Check" className="size-6 text-header" />
    </div>
    <Text variant="h4" className="mt-5 text-header-inverse">
      Thank you, order confirmed!
    </Text>
    <Text variant="base-sm" className="mt-3 max-w-[360px] text-primary-inverse">
      Your budget has been increased by {formatMoney(budget)} and is available to spend.
    </Text>
    <Button variant="active" size="default" onClick={onClose} className="mt-7">
      OK
    </Button>
  </div>
)

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
const DealInfo: React.FC<{ orderItem: OrderItem; cpm: number }> = ({ orderItem, cpm }) => {
  const airDates =
    orderItem.airStartTime !== null && orderItem.airEndTime !== null
      ? {
          start: format(new Date(orderItem.airStartTime), DATE_FORMATS.HOST_APP_DATETIME),
          end: format(new Date(orderItem.airEndTime), DATE_FORMATS.HOST_APP_DATETIME),
        }
      : null
  const programText = getOfferingText(orderItem.programs.length)

  return (
    <div className="flex gap-6 items-center px-4 pb-4 pt-0">
      <div className="border-2 border-white w-[100px] h-[125px] overflow-hidden shrink-0">
        <Image
          src={orderItem.coverMediaUrl || ''}
          alt={orderItem.offeringName}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="flex flex-1 flex-row items-center self-stretch">
        <div className="flex flex-col min-w-0 justify-center">
          <Text variant="h5" className="text-header-inverse truncate">
            {orderItem.offeringName}
          </Text>
          <div className="flex items-end gap-1">
            <Text variant="number-base" className="text-secondary-accent-inverse">
              {formatMoney(cpm)}
            </Text>
            <Text variant="base-xs" className="text-secondary-inverse">
              CPM
            </Text>
          </div>
          <Text variant="xs-sb" className="text-primary-inverse truncate">
            {programText}
          </Text>
          {airDates && (
            <div className="flex flex-col pt-1">
              <DateRow label="Start" value={airDates.start} />
              <DateRow label="End" value={airDates.end} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
const DateRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex gap-1 items-center">
    <Text variant="number-xs" className="text-primary-inverse">
      {label}:
    </Text>
    <Text variant="number-xs" className="text-secondary-inverse">
      {value}
    </Text>
  </div>
)

interface BudgetFormProps {
  budgetInput: string
  impressionsInput: string
  onBudgetChange: (value: string) => void
  onImpressionsChange: (value: string) => void
  onSubmit: () => void
  canSubmit: boolean
  isSubmitting: boolean
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
const BudgetForm: React.FC<BudgetFormProps> = ({
  budgetInput,
  impressionsInput,
  onBudgetChange,
  onImpressionsChange,
  onSubmit,
  canSubmit,
  isSubmitting,
}) => (
  <div className="flex items-end gap-4 px-4 pb-6 w-full">
    {/* Budget + arrow + Impressions */}
    <div className="flex flex-1 items-end self-stretch">
      <div className="flex flex-1 flex-col gap-2 h-full items-start">
        <div className="flex gap-6 items-start w-full">
          <InputField label="Budget">
            <Input
              type="text"
              inputMode="decimal"
              value={budgetInput}
              onChange={e => onBudgetChange(e.target.value)}
              variant="inverse"
              iconLeft={<span className="text-secondary-inverse font-number font-medium">$</span>}
            />
          </InputField>

          <div className="flex flex-col items-center justify-end pb-[16px] pt-[10px] self-stretch shrink-0 text-primary-inverse">
            <Icon name="ArrowLeftRight" className="size-4 text-secondary-inverse" />
          </div>

          <InputField label="Impressions">
            <Input
              type="text"
              inputMode="decimal"
              placeholder="0"
              value={impressionsInput}
              onChange={e => onImpressionsChange(e.target.value)}
              variant="inverse"
            />
          </InputField>
        </div>
      </div>
    </div>

    <Button variant="active" size="default" onClick={onSubmit} disabled={!canSubmit} className="shrink-0 self-end">
      Purchase
      {isSubmitting && <Icon name="LoaderCircle" className="animate-spin size-4 ml-2 text-current" />}
    </Button>
  </div>
)

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
const InputField: React.FC<{
  label: string
  children: React.ReactNode
}> = ({ label, children }) => (
  <div className="flex flex-col gap-2 flex-1 min-w-0">
    <Text variant="h6" className="text-header-inverse">
      {label}
    </Text>
    {children}
  </div>
)
